import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { intercomTarget } from '@/helpers/intercomTarget';
import { getDocumentIdToQueueItemMap, getRejectionsByDocumentId } from '@/helpers/queues';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetPerformProgressQuery } from '@/services/organizations';
import {
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Icon,
  Modal,
  TextLink,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  ApplicationDocumentWithAdditionalApplicantData,
  DocumentDocument,
  parcelProviderLabelsMap,
  PropertyOwnershipStatus,
  ProgramType,
  UnderwritingDocumentTypes,
} from 'types';

import { useVerifyOwnershipForApplicationMutation } from '../../services/applications';
import { useUploadDocumentMutation } from '../../services/documents';
import { useLazyGetPropertyReportsQuery } from '../../services/propertyReports';
import { useGetQueuesByAccountIdAndDocumentTypeQuery } from '../../services/queues';
import { AccountRequirementStatus } from '../../types/Milestone';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { StatusIcon } from '../StatusIcon';

import { DocumentItem } from './DocumentItem';

interface TitleVerificationBodyProps {
  reports: any[];
  isLoading: boolean;
  error: any;
  documents: DocumentDocument[] | undefined;
  documentIdToQueueItemMap: Record<string, any>;
  rejectionsByDocumentId: Map<string, any[]>;
  handleViewReport: (id: string) => void;
  padding?: string;
}

function TitleVerificationBody({
  reports,
  isLoading,
  error,
  documents,
  documentIdToQueueItemMap,
  rejectionsByDocumentId,
  handleViewReport,
  padding,
}: TitleVerificationBodyProps) {
  return (
    <Box padding={padding}>
      {!isLoading &&
        !error &&
        reports.map((report: any) => {
          const locationVerified = report?.result?.propertyInExpectedLocation;
          const ownerVerified = report?.result?.applicantMentionedAsOwner;
          return (
            <Box
              key={report?.id}
              padding="sm 0 sm lg"
              width="100"
              childGap="lg"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              borderWidth="xs 0 0 0"
              borderColor="separator"
            >
              <Box childGap="2xs">
                <Box direction="row" childGap="xs">
                  <Icon
                    name={locationVerified ? 'check' : 'remove'}
                    color={locationVerified ? 'primary' : 'danger'}
                  />
                  Property {locationVerified ? '' : ' not'} location verified
                </Box>
                <Box direction="row" childGap="xs">
                  <Icon name={ownerVerified ? 'check' : 'remove'} color={ownerVerified ? 'primary' : 'danger'} />
                  Primary applicant is {ownerVerified ? '' : ' not'} an owner
                </Box>
                {report?.result?.status === PropertyOwnershipStatus.notFeeSimpleOwnership && (
                  <Box direction="row" childGap="xs" color="danger">
                    <Icon name="remove" color="danger" />
                    Property is held by a legal entity (Trust, LLC, LP, etc.)
                  </Box>
                )}
                {report?.result?.status === PropertyOwnershipStatus.ineligiblePropertyType && (
                  <Box direction="row" childGap="xs" color="danger">
                    <Icon name="remove" color="danger" />
                    Property type is not eligible (manufactured home, multi-family 5+, commercial, etc.)
                  </Box>
                )}
                {report?.result?.status === PropertyOwnershipStatus.verified && (
                  <Box direction="row" childGap="xs" color="info">
                    <Icon name="star-empty" />
                    No further action required
                  </Box>
                )}
                {report?.applicationId && (
                  <Box fontSize="xs" padding="md 0 0 0">
                    For Application Id: {report.applicationId}
                  </Box>
                )}
              </Box>
              <TextLink
                style={{
                  cursor: 'pointer',
                  color: 'var(--color-text-primary)',
                  textDecoration: 'underline',
                }}
                onClick={() => handleViewReport(report?.id)}
              >
                {`${report?.parcelProvider ? parcelProviderLabelsMap[report.parcelProvider] + ' ' : ''}Property Report`}
              </TextLink>
            </Box>
          );
        })}
      {(documents?.length ?? 0) > 0 && !isLoading ? (
        <Box childGap="lg" display="block" padding="md 0 0 0">
          {(documents ?? []).map((doc: DocumentDocument) => {
            const key = doc.id?.toString?.() ?? '';
            const queueItem = documentIdToQueueItemMap[key];
            const rejections = rejectionsByDocumentId.get(key) ?? [];
            return (
              <DocumentItem key={key} item={doc} queue={queueItem} showDocumentActions rejections={rejections} />
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

interface TitleVerificationProps {
  status: AccountRequirementStatus;
  application: ApplicationDocumentWithAdditionalApplicantData;
  creditStatus: AccountRequirementStatus;
  programType: ProgramType;
}
export function TitleVerification({ status, application, creditStatus, programType }: TitleVerificationProps) {
  const { id } = useParams<{ id: any }>();
  const isApproved = status === AccountRequirementStatus.completed;
  const { isOpen: isSectionOpen, handleToggle: handleToggleSection } = useOpenClose({ defaultIsOpen: !isApproved });
  const { isOpen, handleClose, handleOpen: openUploadModal } = useOpenClose();
  const {
    isOpen: isPropertyReportOpen,
    handleClose: handlePropertyModalClose,
    handleOpen: openPropertyModal,
  } = useOpenClose();
  const [files, setFiles] = useState<FileList | undefined>();
  const [selectedReportId, setSelectedReportId] = useState<string>();
  const [selectedReport, setSelectedReport] = useState<Record<string, any> | undefined>();
  const [trigger, { data: reports = [], error, isLoading: isDocumentsLoading }] = useLazyGetPropertyReportsQuery();
  const [uploadDocument] = useUploadDocumentMutation();
  const { data: documents } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: [UnderwritingDocumentTypes.titleVerification],
  });
  const { data: queueItems, isLoading: isGetQueueByAccountIdLoading } = useGetQueuesByAccountIdAndDocumentTypeQuery(
    {
      accountId: id,
      type: UnderwritingDocumentTypes.titleVerification,
    },
    { skip: isApproved && !isSectionOpen },
  );
  const isLoading = isGetQueueByAccountIdLoading || isDocumentsLoading;
  const allowsAction = useIsOrgReadOnly();

  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const documentIdToQueueItemMap = useMemo(() => getDocumentIdToQueueItemMap(queueItems), [queueItems]);
  const rejectionsByDocumentId = useMemo(() => getRejectionsByDocumentId(queueItems), [queueItems]);

  const [verifyOwnershipForApplication, { isLoading: verifyOwnershipIsLoading }] =
    useVerifyOwnershipForApplicationMutation();

  useEffect(() => {
    if (id) {
      trigger({
        accountId: id,
        ...(selectedReportId && { includeReportDetails: true }),
      });
    }
  }, [trigger, id, selectedReportId]);

  useEffect(() => {
    if (selectedReportId) {
      setSelectedReport(reports.find((report: any) => report?.id === selectedReportId));
    }
  }, [selectedReportId, setSelectedReport, reports]);

  const handleViewReport = useCallback(
    (item: string) => {
      setSelectedReportId(item);
      openPropertyModal();
    },
    [openPropertyModal, setSelectedReportId],
  );

  const handleVerifyOwnership = useCallback(() => {
    verifyOwnershipForApplication({ accountId: id, applicationId: application.id });
  }, [verifyOwnershipForApplication, id, application]);

  const onFileChange = (event: any) => {
    setFiles(event.target?.files);
  };

  const closeUploadModal = () => {
    setFiles(undefined);
    handleClose();
  };

  const handleFileUpload = () => {
    if (!files || !files.length) return;

    const data = new FormData();
    data.append('grouped', 'true');
    data.append('type', UnderwritingDocumentTypes.titleVerification);

    for (let i = 0; i < files.length; i++) {
      data.append(`files[${i}]`, files[i]);
    }

    closeUploadModal();

    uploadDocumentsWithToast({
      uploadDocument,
      accountId: id,
      formData: data,
      invalidateAllTags: true,
      files,
      messages: {
        success: () => 'Title verification uploaded',
      },
    });
  };

  const creditApplicationRequired =
    programType === ProgramType.solar ||
    programType === ProgramType.hvac ||
    programType === ProgramType.waterHeater ||
    programType === ProgramType.generator;

  return (
    <>
      <Modal ariaLabelledBy="titlePropertyReport" isOpen={isPropertyReportOpen} onDismiss={handlePropertyModalClose}>
        <Modal.Header
          id="titlePropertyReport"
          title={`Property Report${selectedReport?.parcelProvider ? ' - ' + parcelProviderLabelsMap[selectedReport?.parcelProvider] : ''}`}
          onDismiss={handlePropertyModalClose}
        />
        <Modal.Body>
          <Box background="tertiary" padding="sm">
            {isLoading && <div>Loading...</div>}
            {!isLoading && !error && selectedReport?.response?.Reports && (
              <>
                <Box as="ul" childGap="xs" margin="0 0 md 0" padding="0 0 0 md">
                  {selectedReport?.result?.applicantName && (
                    <li>
                      <code>Applicant Name: {selectedReport.result.applicantName || 'N/A'}</code>
                    </li>
                  )}
                  {selectedReport?.result?.ownerNamesOnParcel && (
                    <li>
                      <code>Owner Names on Report: {selectedReport.result.ownerNamesOnParcel || 'N/A'}</code>
                    </li>
                  )}
                  {Boolean(selectedReport?.result?.distanceBetween) && (
                    <li>
                      <code>
                        Distance from Expected Location (meters): {selectedReport.result.distanceBetween || 'N/A'}
                      </code>
                    </li>
                  )}
                </Box>
                <code>
                  <pre>{JSON.stringify(selectedReport.response.Reports[0], null, 2)}</pre>
                </code>
              </>
            )}
          </Box>
        </Modal.Body>
      </Modal>
      <Modal
        isOpen={isOpen}
        onDismiss={closeUploadModal}
        maxWidth="4xl"
        ariaLabelledBy="uploadTitleVerificationHeader"
      >
        <Modal.Header
          id="uploadTitleVerificationHeader"
          title="Upload Proof of Title"
          onDismiss={closeUploadModal}
        />
        <Modal.Body background="secondary" childGap="md">
          <Box>Approved documents include a copy of:</Box>
          <Box as="ul" childGap="xs">
            <li>Recorded Deed</li>
            <li>Recent mortgage statement</li>
            <li>Most recent property tax bill</li>
          </Box>
          <ResettingFileUpload
            buttonText="Choose File(s)"
            name="files"
            labelText="Choose File(s)"
            id="files"
            variant="secondary"
            tone="neutral"
            multiple
            size="md"
            required
            requiredIndicator=""
            onChange={onFileChange}
            files={files}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeUploadModal}>
            Cancel
          </Button>
          <Button onClick={handleFileUpload} isDisabled={!files || !files.length}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>
      <Card.Section>
        {isApproved ? (
          <Accordion.Panel isOpen={isSectionOpen} borderWidth="0">
            <Accordion.PanelSummary
              isDetailsOpen={isSectionOpen}
              onToggle={handleToggleSection}
              padding="0"
              hasCaret="right"
            >
              <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between" flex="auto">
                <Box direction="row" alignItems="center">
                  <Box padding="0 xs 0 0">
                    <StatusIcon status={status} size="md" />
                  </Box>
                  <Box
                    as="h3"
                    fontWeight="medium"
                    fontSize="md"
                    data-intercom-target={intercomTarget('account', 'title-verification')}
                  >
                    Title Verification
                  </Box>
                </Box>
                <Badge message="APPROVED" variant="success" />
              </Box>
            </Accordion.PanelSummary>
            <Accordion.PanelDetails padding="0">
              <TitleVerificationBody
                reports={reports}
                isLoading={isLoading}
                error={error}
                documents={documents}
                documentIdToQueueItemMap={documentIdToQueueItemMap}
                rejectionsByDocumentId={rejectionsByDocumentId}
                handleViewReport={handleViewReport}
                padding="md 0 0 0"
              />
            </Accordion.PanelDetails>
          </Accordion.Panel>
        ) : (
          <>
            <Box childGap="lg" alignItems="center" direction="row" justifyContent="space-between">
              <Box direction="row" alignItems="center">
                <Box padding="0 xs 0 0">
                  <StatusIcon status={status} size="md" />
                </Box>
                <Box
                  as="h3"
                  fontWeight="medium"
                  fontSize="md"
                  data-intercom-target={intercomTarget('account', 'title-verification')}
                >
                  Title Verification
                </Box>
              </Box>
              {creditApplicationRequired && creditStatus !== 'completed' ? (
                <Box fontWeight="medium" fontSize="sm" color="body-secondary">
                  Pending Credit Application
                </Box>
              ) : status === 'pending' ? (
                <Box>
                  <Button
                    size="sm"
                    disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction || verifyOwnershipIsLoading}
                    style={{ width: 'fit-content' }}
                    onClick={handleVerifyOwnership}
                    isLoading={verifyOwnershipIsLoading}
                    variant="secondary"
                    tone="neutral"
                  >
                    Auto-Verify
                  </Button>
                </Box>
              ) : (
                <Box direction="row" alignItems="center" childGap="sm">
                  <Badge
                    message={
                      status === AccountRequirementStatus.error
                        ? 'Error: Further action required'
                        : 'Further action required'
                    }
                    variant="danger"
                  />
                  <Button
                    size="sm"
                    disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
                    style={{ width: 'fit-content' }}
                    onClick={openUploadModal}
                    variant="secondary"
                    tone="neutral"
                  >
                    Upload Proof of Title
                  </Button>
                </Box>
              )}
            </Box>
            <TitleVerificationBody
              reports={reports}
              isLoading={isLoading}
              error={error}
              documents={documents}
              documentIdToQueueItemMap={documentIdToQueueItemMap}
              rejectionsByDocumentId={rejectionsByDocumentId}
              handleViewReport={handleViewReport}
              padding="md 0 0 0"
            />
          </>
        )}
      </Card.Section>
    </>
  );
}
